/**
 * Kleiner Daten-Hook ueber apiFetch: laedt GET-Endpunkte, liefert {data, loading, error, reload}.
 * Bewusst ohne Cache/Query-Lib — die Listen sind klein, Einfachheit schlaegt Infrastruktur.
 */
import { useCallback, useEffect, useState } from "react";
import { apiFetch, toApiError } from "../lib/api";

interface ApiEnvelope<T> {
  data: T;
  total?: number;
}

export function useApi<T>(path: string | null): {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(path != null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (path == null) return;
    // H6: AbortController pro Effect-Aufruf statt eines ueber Renderzyklen geteilten Refs -
    // sonst kann eine veraltete Antwort (path A) den bereits geladenen State von path B
    // ueberschreiben, wenn A langsamer antwortet, aber der geteilte "alive"-Ref zwischenzeitlich
    // durch den B-Effect wieder auf true gesetzt wurde.
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await apiFetch(path, { signal: controller.signal });
        if (!res.ok) throw await toApiError(res);
        const body = (await res.json()) as ApiEnvelope<T>;
        setData(body.data);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [path, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, reload };
}

/** Mutations-/Lese-Helfer (GET/POST/PUT/PATCH/DELETE): wirft bei nicht-OK, liefert das data-Feld. */
export async function apiMutate<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const res = await apiFetch(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const json = (await res.json()) as { data: T };
  return json.data;
}
